import React from "react";
import { Inbox } from "lucide-react";

// ---- Semantic pill badge -------------------------------------------------
// tone: success | warning | danger | info | neutral
export function SemanticBadge({ tone = "neutral", dot = false, children, testid }) {
  return (
    <span data-testid={testid} className={`badge badge-${tone}`}>
      {dot && <span className="badge-dot" />}
      {children}
    </span>
  );
}

// Map a signed number to a semantic tone (green positive / red negative)
export function AmountBadge({ value, format = (v) => v, testid }) {
  const tone = value > 0 ? "success" : value < 0 ? "danger" : "neutral";
  return <span data-testid={testid} className={`badge badge-${tone} lx-num`}>{format(value)}</span>;
}

// ---- Icon action button (with hover tooltip via title) -------------------
// variant: default | danger | accent
export function IconButton({ icon: Icon, title, onClick, variant = "default", testid, size = 15, disabled }) {
  const cls = variant === "danger" ? "icon-btn icon-btn-danger" : variant === "accent" ? "icon-btn icon-btn-accent" : "icon-btn";
  return (
    <button type="button" title={title} aria-label={title} data-testid={testid} disabled={disabled}
      onClick={onClick} className={`${cls} disabled:opacity-40`}>
      <Icon size={size} />
    </button>
  );
}

// ---- KPI card + strip (matches Dashboard KPI style) ----------------------
// tone: default | accent | success | warning | danger | info
export function KpiCard({ icon: Icon, label, value, sub, tone = "default", testid }) {
  const toneCls = {
    accent: "text-[#1B4383]", success: "text-[#16A34A]", warning: "text-[#D97706]",
    danger: "text-[#DC2626]", info: "text-[#2563EB]", default: "text-gray-900",
  }[tone] || "text-gray-900";
  const len = String(value ?? "").length;
  const fit = len > 22 ? "text-sm" : len > 17 ? "text-base" : len > 13 ? "text-lg" : len > 10 ? "text-xl" : "text-2xl";
  return (
    <div data-testid={testid} className="min-w-0 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="lx-label">{label}</span>
        {Icon && <Icon size={18} className="text-gray-400" />}
      </div>
      <div className={`mt-2 truncate font-display ${fit} font-bold lx-num ${toneCls}`} title={String(value ?? "")}>{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

export function KpiStrip({ children, cols = 4 }) {
  const colCls = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4", 5: "sm:grid-cols-3 lg:grid-cols-5" }[cols] || "sm:grid-cols-4";
  return <div className={`mb-5 grid grid-cols-1 gap-3 ${colCls}`} data-testid="kpi-strip">{children}</div>;
}

// ---- In-table empty state (drop straight into a <tbody>) -----------------
export function TableEmpty({ colSpan = 1, icon: Icon = Inbox, title, message, ctaLabel, onCta, testid }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-12">
        <div data-testid={testid || "table-empty"} className="flex flex-col items-center justify-center text-center">
          <div className="mb-3 grid h-11 w-11 place-items-center rounded-full bg-gray-100 text-gray-400"><Icon size={22} /></div>
          {title && <div className="font-display text-sm font-semibold text-gray-700">{title}</div>}
          {message && <p className="mt-1 max-w-md text-sm text-gray-500">{message}</p>}
          {ctaLabel && onCta && (
            <button onClick={onCta} data-testid="table-empty-cta" className="mt-3 rounded-md bg-[#1B4383] px-3.5 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[#122E5C]">{ctaLabel}</button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ---- Titled section card (used to group content inside tabbed detail pages) ----
export function SectionCard({ title, actions, children, className = "", testid }) {
  return (
    <div data-testid={testid} className={`rounded-lg border border-gray-200 bg-white shadow-sm ${className}`}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-5 py-3">
          {title ? <h3 className="font-display text-sm font-semibold text-gray-800">{title}</h3> : <span />}
          {actions}
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}


export function EmptyState({ icon: Icon, title, message, ctaLabel, onCta, testid }) {
  return (
    <div data-testid={testid || "empty-state"} className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-white px-6 py-14 text-center">
      {Icon && <div className="mb-3 grid h-12 w-12 place-items-center rounded-full bg-gray-100 text-gray-400"><Icon size={24} /></div>}
      <div className="font-display text-base font-semibold text-gray-800">{title || "Nothing here yet"}</div>
      {message && <p className="mt-1 max-w-sm text-sm text-gray-500">{message}</p>}
      {ctaLabel && onCta && (
        <button onClick={onCta} data-testid="empty-state-cta" className="mt-4 rounded-md bg-[#1B4383] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#122E5C]">{ctaLabel}</button>
      )}
    </div>
  );
}
